import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { isoDate } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";
import { REPORT_STATUSES } from "../fleet-services/reports-db/schema";
import { ListReportsQueryParams } from "./list-reports";

/** One site of a scoped RoadSense report, as the caller sees it. */
export const ScopedRoadsenseReportSite = z.object({
  siteId: z.string(),
  siteName: z.string(),
  /** Whether the caller sees only some of the site's movements. */
  partial: z.boolean(),
});
export type ScopedRoadsenseReportSite = z.output<typeof ScopedRoadsenseReportSite>;

/**
 * One RoadSense report in the caller's camera scope. Unlike `ReportListItem` it
 * carries no `listingSummary`, S3 key or whole-report site count: those describe
 * every site in the report.
 */
export const ScopedRoadsenseReportListItem = z.object({
  reportId: z.string(),
  periodStart: isoDate,
  periodEnd: isoDate,
  status: z.enum(REPORT_STATUSES),
  generatedAt: z.iso.datetime().optional(),
  customerName: z.string(),
  /** The sites with at least one movement in the caller's scope, in job order. */
  sites: z.array(ScopedRoadsenseReportSite),
  /**
   * Whether the lifecycle has closed: the stored job's `isFinal`, or for a
   * tenant admin the listing summary's when it has one. Absent when unknown.
   */
  isFinal: z.boolean().optional(),
});
export type ScopedRoadsenseReportListItem = z.output<typeof ScopedRoadsenseReportListItem>;

export const ListScopedRoadsenseReportsResponse = z.object({
  data: z.array(ScopedRoadsenseReportListItem),
  cursor: z.string().optional(),
});
export type ListScopedRoadsenseReportsResponse = z.output<typeof ListScopedRoadsenseReportsResponse>;

/**
 * GET /v2/tenants/{tenantId}/features/reports/roadsense/reports
 *
 * The tenant's RoadSense reports that hold at least one movement the caller
 * can view, newest-first, open to any user with access in the tenant. Takes the
 * same query as `{type}/list`. Rows outside the caller's scope are dropped after
 * the page is read, so a page can hold fewer than `limit` rows while `cursor`
 * still points at more.
 */
export const scopedRoadsenseReportsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "roadsense/reports",
  scope: "tenant",
});

export const listScopedRoadsenseReportsEndpoint = defineEndpoint(scopedRoadsenseReportsResource, {
  responseSchema: ListScopedRoadsenseReportsResponse,
  querySchema: ListReportsQueryParams,
});
